import React, { useState, useEffect, useCallback } from "react";
import {
  Bell,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  Trash2,
  Filter,
  Search,
  RefreshCw,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Check,
  Settings,
  Loader,
  X,
  AlertTriangle,
  Info,
  Menu,
  Grid,
  List,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "@/contexts/notification/NotificationContext";
import { notificationService } from "@/services/api/notification.service";

// Toast Notification Component
const Toast = ({ message, type = "info", onClose, duration = 5000 }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for fade-out animation
    }, duration);

    return () => clearTimeout(timer);
  }, [onClose, duration]);

  const getToastStyles = () => {
    switch (type) {
      case "success":
        return "bg-emerald-50 border-emerald-200 text-emerald-800 shadow-emerald-100";
      case "error":
        return "bg-red-50 border-red-200 text-red-800 shadow-red-100";
      case "warning":
        return "bg-amber-50 border-amber-200 text-amber-800 shadow-amber-100";
      default:
        return "bg-blue-50 border-blue-200 text-blue-800 shadow-blue-100";
    }
  };

  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircle size={20} />;
      case "error":
        return <AlertCircle size={20} />;
      case "warning":
        return <AlertTriangle size={20} />;
      default:
        return <Info size={20} />;
    }
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 border-2 p-4 rounded-xl shadow-xl max-w-sm backdrop-blur-sm bg-opacity-95 transform transition-all duration-300 ease-out ${getToastStyles()} ${isVisible
          ? "translate-x-0 opacity-100"
          : "translate-x-full opacity-0"
        }`}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <p className="font-medium text-sm">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-current opacity-70 hover:opacity-100 transition-opacity p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
}) => {
  if (!isOpen) return null;

  const getModalStyles = () => {
    switch (type) {
      case "danger":
        return "text-red-600";
      case "success":
        return "text-emerald-600";
      default:
        return "text-amber-600";
    }
  };

  const getButtonStyles = () => {
    switch (type) {
      case "danger":
        return "bg-red-600 hover:bg-red-700 focus:ring-red-500";
      case "success":
        return "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500";
      default:
        return "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-4 mb-6">
            <div
              className={`p-3 rounded-full bg-opacity-10 ${type === "danger"
                  ? "bg-red-100"
                  : type === "success"
                    ? "bg-emerald-100"
                    : "bg-amber-100"
                }`}
            >
              {type === "danger" ? (
                <AlertTriangle className={getModalStyles()} size={28} />
              ) : type === "success" ? (
                <CheckCircle className={getModalStyles()} size={28} />
              ) : (
                <AlertTriangle className={getModalStyles()} size={28} />
              )}
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-900">
              {title}
            </h3>
          </div>
          <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-sm sm:text-base">
            {message}
          </p>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 sm:px-6 py-2 sm:py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-semibold text-sm order-2 sm:order-1"
            >
              {cancelText}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              className={`px-4 sm:px-6 py-2 sm:py-3 text-white rounded-xl transition-all duration-200 font-semibold focus:outline-none focus:ring-4 focus:ring-opacity-50 order-1 sm:order-2 text-sm ${getButtonStyles()}`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions for safe data handling
const safeString = (value, defaultValue = "") =>
  value != null ? String(value) : defaultValue;

const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return !isNaN(num) ? num : defaultValue;
};

const safeArray = (value, defaultValue = []) =>
  Array.isArray(value) ? value : defaultValue;

const safeObject = (value, defaultValue = {}) =>
  value && typeof value === "object" && !Array.isArray(value)
    ? value
    : defaultValue;

// Enhanced Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 2) {
        endPage = Math.min(totalPages - 1, 4);
      } else if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - 3);
      }

      if (startPage > 2) {
        pages.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex items-center justify-center space-x-1">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`p-2 sm:p-3 rounded-xl transition-all duration-200 ${
          currentPage === 1
            ? "text-gray-300 cursor-not-allowed"
            : "text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
          }`}
        aria-label="Previous page"
      >
        <ChevronLeft size={18} />
      </button>

      <div className="hidden sm:flex items-center space-x-1">
        {getPageNumbers().map((page, index) => (
          <React.Fragment key={index}>
            {page === "..." ? (
              <span className="px-4 py-2 text-gray-500 font-medium">...</span>
            ) : (
              <button
                onClick={() => onPageChange(page)}
                className={`px-3 sm:px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${currentPage === page
                    ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200"
                    : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
                  }`}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? "page" : undefined}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Mobile pagination info */}
      <div className="sm:hidden px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-xl">
        {currentPage} / {totalPages}
      </div>

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`p-2 sm:p-3 rounded-xl transition-all duration-200 ${currentPage === totalPages
            ? "text-gray-300 cursor-not-allowed"
            : "text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
          }`}
        aria-label="Next page"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
};

const AdminNotifications = () => {
  // Context with safe defaults
  const {
    notifications = [],
    pagination = { page: 1, limit: 10, total: 0, pages: 1 },
    loading = false,
    error = null,
    filters = {},
    fetchadminNotification = () => {},
    markAsRead = () => {},
    markAllAsRead = () => {},
    deleteNotification = () => {},
    updateFilters = () => {},
    setPage = () => {},
  } = useNotification() || {};

  // Local state
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState();
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // list or grid
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Toast and Modal state
  const [toasts, setToasts] = useState([]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "warning",
    confirmText: "Confirm",
    cancelText: "Cancel",
  });

  const handleNotificationClick = async (notification) => {
    const safeNotif = safeObject(notification);
    const id = safeString(safeNotif._id);

    // Mark as read if unread
    if (!safeNotif.isRead) {
      try {
        await markAsRead([id]);
      } catch (err) {
        showToast("Failed to mark notification as read", "error");
      }
    }

    // Navigate based on actionUrl if it exists
    if (safeNotif.actionUrl) {
      navigate(safeNotif.actionUrl);
    } else {
      // Fallback to default route if no actionUrl is provided
      navigate("/admindashboard/all-users");
    }
  };

  // Toast management functions
  const showToast = (message, type = "info", duration = 5000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    setToasts((prev) => [...prev, newToast]);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Confirmation modal function
  const showConfirmation = ({
    title,
    message,
    onConfirm,
    type = "warning",
    confirmText = "Confirm",
    cancelText = "Cancel",
  }) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      type,
      confirmText,
      cancelText,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Initial fetch - only run once on mount
  useEffect(() => {
    let isMounted = true;

    const initialFetch = async () => {
      if (!hasInitialLoad && fetchadminNotification) {
        try {
          await fetchadminNotification();
          if (isMounted) {
            setHasInitialLoad(true);
          }
        } catch (err) {
          showToast("Failed to load notifications", "error");
        }
      }
    };

    initialFetch();

    return () => {
      isMounted = false;
    };
  }, []);

  // Manual refresh function
  const handleRefresh = useCallback(async () => {
    try {
      await fetchadminNotification();
      showToast("Notifications refreshed successfully", "success");
    } catch (err) {
      showToast("Failed to refresh notifications", "error");
    }
  }, [fetchadminNotification]);

  // Handle notification selection
  const handleNotificationSelect = (notificationId) => {
    setSelectedNotifications((prev) =>
      prev.includes(notificationId)
        ? prev.filter((id) => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  // Handle select all
  const handleSelectAll = () => {
    const currentPageIds = notifications.map((n) => safeString(n._id));
    const hasAllCurrentSelected = currentPageIds.every((id) =>
      selectedNotifications.includes(id)
    );

    if (hasAllCurrentSelected) {
      // Unselect all from current page
      setSelectedNotifications((prev) =>
        prev.filter((id) => !currentPageIds.includes(id))
      );
    } else {
      // Select all from current page (add to existing selections)
      setSelectedNotifications((prev) => {
        const newSelections = currentPageIds.filter((id) => !prev.includes(id));
        return [...prev, ...newSelections];
      });
    }
  };

  // Handle mark selected as read
  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.length === 0) return;

    try {
      await markAsRead(selectedNotifications);
      setSelectedNotifications([]);
      showToast(
        `${selectedNotifications.length} notification(s) marked as read`,
        "success"
      );
    } catch (err) {
      showToast("Failed to mark notifications as read", "error");
    }
  };

  // Handle delete selected
  const handleDeleteSelected = async () => {
    if (selectedNotifications.length === 0) return;

    showConfirmation({
      title: "Delete Notifications",
      message: `Are you sure you want to delete ${selectedNotifications.length} notification(s)? This action cannot be undone.`,
      type: "danger",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await Promise.all(
            selectedNotifications.map((id) => deleteNotification(id))
          );
          setSelectedNotifications([]);
          showToast(
            `${selectedNotifications.length} notification(s) deleted successfully`,
            "success"
          );
        } catch (err) {
          showToast("Failed to delete notifications", "error");
        }
      },
    });
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  // Handle filter change
  const handleFilterChange = (filterKey, value) => {
    updateFilters({ [filterKey]: value });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSelectedNotifications([]); // Add this line
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    showConfirmation({
      title: "Mark All as Read",
      message: "Are you sure you want to mark all notifications as read?",
      type: "success",
      confirmText: "Mark All Read",
      onConfirm: async () => {
        try {
          await markAllAsRead();
          showToast("All notifications marked as read", "success");
        } catch (err) {
          showToast("Failed to mark all as read", "error");
        }
      },
    });
  };

  // Handle single notification actions
  const handleSingleMarkAsRead = async (id, isCurrentlyRead) => {
    try {
      await markAsRead([id]);
      setActiveDropdown(null);
      showToast(
        isCurrentlyRead
          ? "Notification marked as unread"
          : "Notification marked as read",
        "success"
      );
    } catch (err) {
      showToast("Failed to update notification", "error");
    }
  };

  const handleSingleDelete = async (id) => {
    showConfirmation({
      title: "Delete Notification",
      message:
        "Are you sure you want to delete this notification? This action cannot be undone.",
      type: "danger",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await deleteNotification(id);
          setActiveDropdown(null);
          showToast("Notification deleted successfully", "success");
        } catch (err) {
          showToast("Failed to delete notification", "error");
        }
      },
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid date";

      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    } catch {
      return "Invalid date";
    }
  };

  // Get notification type badge color
  const getTypeColor = (type) => {
    const colors = {
      system: "bg-blue-100 text-blue-800 border-blue-200",
      user: "bg-emerald-100 text-emerald-800 border-emerald-200",
      transaction: "bg-purple-100 text-purple-800 border-purple-200",
      kyc: "bg-amber-100 text-amber-800 border-amber-200",
      portfolio: "bg-indigo-100 text-indigo-800 border-indigo-200",
      appointment: "bg-pink-100 text-pink-800 border-pink-200",
      wallet: "bg-orange-100 text-orange-800 border-orange-200",
      general: "bg-gray-100 text-gray-800 border-gray-200",
    };
    return colors[type] || colors.general;
  };

  // Safe values
  const safePagination = safeObject(pagination);
  const safeFilters = safeObject(filters);

  // NotificationCard component for grid view
  const NotificationCard = ({ notification }) => {
    const safeNotif = safeObject(notification);
    const id = safeString(safeNotif._id);

    return (
      <div
        onClick={() => handleNotificationClick(notification)}
        className={`bg-white rounded-2xl border-2 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1 ${!safeNotif.isRead
            ? "border-blue-200 bg-gradient-to-br from-blue-50 to-white"
            : "border-gray-200 hover:border-gray-300"
          }`}
      >
        <div className="p-4 sm:p-6">
          <div className="flex items-start justify-between mb-4">
            <input
              type="checkbox"
              checked={selectedNotifications.includes(id)}
              onChange={() => handleNotificationSelect(id)}
              className="mt-1 rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500 focus:ring-2"
            />
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Add this line
                  setActiveDropdown(activeDropdown === id ? null : id);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
              >
                <MoreVertical size={18} />
              </button>

              {activeDropdown === id && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 shadow-xl rounded-xl z-10 overflow-hidden">
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Add this
                      handleSingleMarkAsRead(id, safeNotif.isRead);
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-sm font-medium text-gray-700 transition-all"
                  >
                    {safeNotif.isRead ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                    {safeNotif.isRead ? "Mark Unread" : "Mark Read"}
                  </button>
                  <button
                    onClick={() => handleSingleDelete(id)}
                    className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 flex items-center gap-3 text-sm font-medium transition-all"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 mb-3">
            <h4 className="font-bold text-gray-900 text-base sm:text-lg line-clamp-2">
              {safeString(safeNotif.title)}
            </h4>
            {!safeNotif.isRead && (
              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
            )}
          </div>

          <p className="text-gray-700 mb-4 leading-relaxed text-sm sm:text-base line-clamp-3">
            {safeString(safeNotif.message)}
          </p>

          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span
              className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold border ${getTypeColor(
                safeNotif.type
              )}`}
            >
              {safeString(safeNotif.type, "general")}
            </span>
            {safeNotif.priority && (
              <span
                className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold border ${safeNotif.priority === "high"
                    ? "bg-red-100 text-red-800 border-red-200"
                    : safeNotif.priority === "medium"
                      ? "bg-amber-100 text-amber-800 border-amber-200"
                      : "bg-gray-100 text-gray-800 border-gray-200"
                  }`}
              >
                {safeString(safeNotif.priority)} priority
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Clock size={12} />
              <span className="hidden sm:inline">
                {formatDate(safeNotif.createdAt)}
              </span>
              <span className="sm:hidden">
                {formatDate(safeNotif.createdAt).split(",")[0]}
              </span>
            </div>
            {safeNotif.userId && (
              <div className="flex items-center gap-1">
                <Users size={12} />
                <span className="truncate">
                  ID: {safeString(safeNotif.userId)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Toast Container - Fixed position outside main content */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      <div className="p-3 sm:p-4 lg:p-6 xl:p-8 max-w-7xl mx-auto">
        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={closeConfirmModal}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.confirmText}
          cancelText={confirmModal.cancelText}
        />

        {/* Header - Fully Responsive */}
        <div className="mb-6 sm:mb-8 lg:mb-12">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 sm:gap-6">
            <div>
              <h1 className="text-xl sm:text-xl lg:text-2xl xl:text-4xl font-black text-gray-900 tracking-tight mb-2 sm:mb-3 bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                Admin Notifications
              </h1>
              <p className="text-gray-600 font-medium text-sm sm:text-base lg:text-lg">
                Manage and monitor all system notifications and user activities
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="bg-white px-4 sm:px-6 py-3 sm:py-4 rounded-2xl shadow-lg border border-gray-200 backdrop-blur-sm bg-opacity-95">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-2 sm:w-3 h-2 sm:h-3 bg-emerald-400 rounded-full animate-pulse shadow-lg shadow-emerald-200"></div>
                  <span className="text-base sm:text-lg font-bold text-gray-800">
                    {safeNumber(safePagination.total)} Total
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls - Responsive */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:justify-between gap-4 lg:gap-6">
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-200 rounded-2xl shadow-lg bg-white text-gray-800 hover:bg-gray-50 hover:border-indigo-200 focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all font-semibold text-sm"
              >
                <Filter
                  size={16}
                  className="mr-1 sm:mr-2 text-indigo-500"
                />
                <span className="hidden sm:inline">Filter</span>
                {showFilters ? (
                  <ChevronUp size={16} className="ml-1 sm:ml-2" />
                ) : (
                  <ChevronDown
                    size={16}
                    className="ml-1 sm:ml-2"
                  />
                )}
              </button>

              <button
                onClick={handleRefresh}
                disabled={loading}
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-200 rounded-2xl shadow-lg bg-white text-gray-800 hover:bg-gray-50 hover:border-indigo-200 focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all font-semibold text-sm disabled:opacity-50"
              >
                <RefreshCw
                  size={16}
                  className={`mr-1 sm:mr-2 text-indigo-500 ${
                    loading ? "animate-spin" : ""
                    }`}
                />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              {/* Mobile menu toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden inline-flex items-center px-4 py-3 border-2 border-gray-200 rounded-2xl shadow-lg bg-white text-gray-800 hover:bg-gray-50 transition-all"
              >
                <Menu size={18} />
              </button>
            </div>

            <div
              className={`flex flex-col sm:flex-row gap-3 ${
                isMobileMenuOpen ? "block" : "hidden lg:flex"
                }`}
            >
              <button
                onClick={handleMarkAllAsRead}
                disabled={loading}
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-transparent rounded-2xl shadow-lg bg-gradient-to-r from-indigo-600 to-indigo-700 text-white hover:from-indigo-700 hover:to-indigo-800 focus:outline-none focus:ring-4 focus:ring-indigo-200 transition-all font-semibold text-sm disabled:opacity-50 min-w-[160px]"
              >
                <Check size={18} className="mr-2" />
                <span>Mark All Read</span>
              </button>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white p-6 rounded-xl mb-6 shadow-md border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Filter Notifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Notification Type
                </label>
                <select
                  value={safeString(safeFilters.type)}
                  onChange={(e) =>
                    handleFilterChange("type", e.target.value || null)
                  }
                  className="block w-full pl-4 pr-10 py-3 text-base border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm"
                >
                  <option value="">All Types</option>
                  <option value="system">System</option>
                  <option value="user">User</option>
                  <option value="transaction">Transaction</option>
                  <option value="kyc">KYC</option>
                  <option value="portfolio">Portfolio</option>
                  <option value="appointment">Appointment</option>
                  <option value="wallet">Wallet</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">
                  Read Status
                </label>
                <select
                  value={safeFilters.isRead === null ? "" : safeFilters.isRead}
                  onChange={(e) =>
                    handleFilterChange(
                      "isRead",
                      e.target.value === "" ? null : e.target.value === "true"
                    )
                  }
                  className="block w-full pl-4 pr-10 py-3 text-base border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm"
                >
                  <option value="">All Status</option>
                  <option value="false">Unread</option>
                  <option value="true">Read</option>
                </select>
              </div>
            </div>
          </div>
        )}


        {/* Bulk Actions */}
        {selectedNotifications.length > 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-indigo-800 font-medium">
                  {selectedNotifications.length} notification
                  {selectedNotifications.length > 1 ? "s" : ""} selected
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleMarkSelectedAsRead}
                  className="inline-flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-all font-medium text-sm"
                >
                  <Eye size={14} className="mr-1" />
                  Mark Read
                </button>
                <button
                  onClick={handleDeleteSelected}
                  className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all font-medium text-sm"
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={
                  notifications.length > 0 &&
                  notifications.every((n) =>
                    selectedNotifications.includes(safeString(n._id))
                  )
                }
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-4"
              />
              <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Notifications Management
              </span>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center p-12">
              <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
              <p className="text-gray-600 font-medium">
                Loading notifications...
              </p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="bg-red-100 rounded-full p-3 mb-4">
                <AlertCircle size={28} className="text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Unable to load notifications
              </h3>
              <p className="text-gray-600 mb-4 max-w-md">
                We encountered an error while loading notifications. Please try
                again.
              </p>
              <button
                onClick={handleRefresh}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium text-sm"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <Bell className="text-gray-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                No notifications found
              </h3>
              <p className="text-gray-600 max-w-md">
                There are no notifications matching your current filters. Try
                adjusting your search criteria.
              </p>
            </div>
          )}

          {/* Notifications */}
          {!loading && !error && notifications.length > 0 && (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const safeNotif = safeObject(notification);
                const id = safeString(safeNotif._id);

                return (
                  <div
                    key={id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 sm:p-6 transition-all duration-200 hover:bg-gray-50 rounded-xl cursor-pointer ${!safeNotif.isRead
                        ? "bg-blue-50 border-l-4 border-l-blue-500"
                        : ""
                      }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h4 className="font-semibold text-gray-900 text-base sm:text-lg">
                                {safeString(safeNotif.title)}
                              </h4>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(
                                  safeNotif.type
                                )}`}
                              >
                                {safeString(safeNotif.type, "general")}
                              </span>
                              {!safeNotif.isRead && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              )}
                            </div>

                            <p className="text-gray-700 mb-3 text-sm sm:text-base leading-relaxed">
                              {safeString(safeNotif.message)}
                            </p>

                            <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-500">
                              <div className="flex items-center gap-1">
                                <Clock size={14} />
                                {formatDate(safeNotif.createdAt)}
                              </div>

                              {safeNotif.priority && (
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${safeNotif.priority === "high"
                                      ? "bg-red-100 text-red-800"
                                      : safeNotif.priority === "medium"
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-gray-100 text-gray-800"
                                    }`}
                                >
                                  {safeString(safeNotif.priority)} priority
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Dropdown */}
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Add this line
                                setActiveDropdown(
                                  activeDropdown === id ? null : id
                                );
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                            >
                              <MoreVertical size={18} />
                            </button>

                            {activeDropdown === id && (
                              <div className="absolute right-0 mt-2 w-44 sm:w-48 bg-white border border-gray-200 shadow-xl rounded-lg z-20 overflow-hidden">
                                <button
                                  onClick={async () => {
                                    await markAsRead([id]);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3 text-sm font-medium text-gray-700 transition-all"
                                >
                                  {safeNotif.isRead ? (
                                    <EyeOff size={16} />
                                  ) : (
                                    <Eye size={16} />
                                  )}
                                  {safeNotif.isRead
                                    ? "Mark Unread"
                                    : "Mark Read"}
                                </button>
                                <button
                                  onClick={async () => {
                                    await deleteNotification(id);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 flex items-center gap-3 text-sm font-medium transition-all"
                                >
                                  <Trash2 size={16} />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && notifications.length > 0 && (
          <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <Pagination
              currentPage={safePagination.page}
              totalPages={safePagination.pages}
              onPageChange={handlePageChange}
            />
            <div className="text-sm text-gray-600 font-medium">
              Showing{" "}
              <span className="font-semibold text-gray-800">
                {(safePagination.page - 1) * safePagination.limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-gray-800">
                {Math.min(
                  safePagination.page * safePagination.limit,
                  safePagination.total
                )}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800">
                {safePagination.total}
              </span>{" "}
              notifications
            </div>
          </div>
        )}

        {/* Click outside handler for dropdown */}
        {activeDropdown && (
          <div
            className="fixed inset-0 z-0"
            onClick={() => setActiveDropdown(null)}
          />
        )}
      </div>
    </div>
  );
};

export default AdminNotifications;
